import { useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@ooh/contracts';
import { ROLE_GROUPS, type RoleGroupDef } from './role-groups';
import type { RoleSidebarGroup } from './role-sidebar';

function matchesSearch(role: Role | undefined, code: string, query: string): boolean {
  if (!role) return false;
  return role.name.toLowerCase().includes(query) || code.includes(query);
}

function filterGroups(
  translated: RoleSidebarGroup[],
  roles: Role[],
  query: string,
): RoleSidebarGroup[] {
  return translated
    .map((group) => ({
      ...group,
      roleCodes: group.roleCodes.filter((code) =>
        matchesSearch(
          roles.find((r) => r.code === code),
          code,
          query,
        ),
      ),
    }))
    .filter((group) => group.roleCodes.length > 0);
}

export interface RoleSidebarGroupsState {
  groups: RoleGroupDef[];
  setGroups: Dispatch<SetStateAction<RoleGroupDef[]>>;
  translatedGroups: RoleSidebarGroup[];
  filteredGroups: RoleSidebarGroup[];
}

/**
 * Owns the static role-group sidebar state: the (locally mutable, in-memory
 * only — see `role-groups.ts`) group list, its translated labels, and the
 * search-filtered view `RoleSidebar` renders.
 */
export function useRoleSidebarGroups(roles: Role[], searchQuery: string): RoleSidebarGroupsState {
  const { t } = useTranslation('admin');
  const [groups, setGroups] = useState<RoleGroupDef[]>(ROLE_GROUPS);

  const translatedGroups: RoleSidebarGroup[] = useMemo(
    () => groups.map((g) => ({ id: g.id, label: t(g.labelKey), roleCodes: g.roleCodes })),
    [groups, t],
  );

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return translatedGroups;
    return filterGroups(translatedGroups, roles, query);
  }, [translatedGroups, roles, searchQuery]);

  return { groups, setGroups, translatedGroups, filteredGroups };
}
